import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Button, FormControl, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import IComponent from '../../../../services/forms.interface';
import classNames from 'classnames';
import { ALLERGIESPMS, SWITCHINPUT } from '../constants';

interface ICustomProps {
    name: string;
    type: string;
    value: string;
    defaultValue: string;
    disabled: boolean;
    read_only: string;
    required: boolean;
    maskKey: string;
    error: any;
    onChange: any;
    onBlur: any;
    label: string;
    key: string;
    buttonDisabled: string;
    isMobile: boolean;
}

interface IPmsListItemProps {
    id: string;
    parentId: string;
    label: string;
    isMobile?: boolean;
    disabled: boolean;
    handleChange: (event: any, id: string, value: string, parentId: string) => void;
    index: number;
    value?: string;
    error?: string;
    required?: boolean;
    childComponents?: Map<string, IComponent>;
}

const useStyles = makeStyles((theme) => ({
    pmsListContainer: {
        display: 'flex',
        flexDirection: 'column',
        flexBasis: '100%',
        border: '1px solid #ccc',
        borderRadius: '7px',
        paddingBottom: '20px',
        backgroundColor: '#fff',
        marginBottom: '10px',
    },
    subHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px solid #ccc',
        padding: '10px',
        color: '#1C3D64',
        marginBottom: '15px',
        paddingLeft: '20px',
        flexDirection: (props: any) => props.isMobile ? 'column' : 'row',
    },
    pmsListItem: {
        marginLeft: '10px',
    },
    addIcon: {
        width: '30px',
    },
    addDiv: {
        flexBasis: '30px',
        minWidth: '30px',
        jusifyContent: 'flex-end'
    },
    textFieldName: {
        marginBottom: '10px',
    },
    noToAllButton: {
        width: (props: any) => props.isMobile ? '100%' : 'auto',
    },
    row: {
        display: 'flex',
        flexGrow: 1,
        flexBasis: '100%',
        flexWrap: 'wrap',
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottom: (props: any) => props.isMobile ? 'none'  : '1px solid #ccc',
        paddingTop: (props: any) => props.isMobile ? '0px' : '10px',
        paddingBottom: (props: any) => props.isMobile ? '0px' : '10px',
        marginRight: (props: any) => props.isMobile ? '5px' : '40px',
    },
    column1: {
        display: 'flex',
        flexDirection: 'row',
        flexBasis: (props: any) => props.isMobile ? '100%' : '45%',
        paddingTop: (props: any) => props.isMobile ? '10px' : '0px',
        paddingBottom: (props: any) => props.isMobile ? '10px' : '0px',
    },
    column2: {
        display: 'flex',
        flexDirection: 'row',
        flexBasis: (props: any) => props.isMobile ? '100%' : '45%',
        paddingTop: (props: any) => props.isMobile ? '10px' : '0px',
        paddingBottom: (props: any) => props.isMobile ? '10px' : '0px',
    },
    button: {
        backgroundColor: '#F9F9F9',
        outlineColor: 'rgba(0, 0, 0, 0.23)',
        outlineWidth: '1px',
        outlineStyle: 'solid',
        margin: '1px',
        '&.MuiToggleButton-root.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        },
        '&.error': {
            color: theme.palette.error.main,
            outlineColor: theme.palette.error.main,
        }
    },
    formControl: {
        display: 'flex',
        flexGrow: 1,
        justifyContent: 'space-between',
        flexDirection: (props: any) => props.isMobile ? 'column' : 'row',
        flexWrap: 'nowrap',
        flexBasis: '100%',
     

    },
    textField: {
        minWidth: '300px',
        marginLeft: '5px'
    },
    mobileTextField: {
        minWidth: '300px',
        marginTop: '5px'
    },
    mobile: {
        flexWrap: 'wrap',
        flexDirection: 'column'
    },
    label: {
        marginRight: '20px'
    }
}));


const PmsListItem: React.FC<IPmsListItemProps> = props => {
    const classes = useStyles({ isMobile: props.isMobile });
    const switchValue = props.value ? props.value : '';
    const childError = props.childComponents && props.childComponents[props.id].hasOwnProperty('error') ? true : false;
   
    const handleChange = (event, newValue) => {
        props.handleChange(event, props.id, newValue, props.parentId);
    };

    return (
        <FormControl component="fieldset" className={`${classes.formControl} ${props.isMobile ? classes.mobile : ''}`} required={props.required}>

            <Typography variant="subtitle1" fontSize={17} className={classes.textFieldName}>
                    {props.label}
                </Typography>

                <ToggleButtonGroup value={switchValue}
                    exclusive
                    id={props.id}
                    size="small"
                    disabled={props.disabled}
                    onChange={handleChange}
                >
                    <ToggleButton value="true" className={classNames(classes.button, { error: switchValue === '' && childError })}>Yes</ToggleButton>,
                    <ToggleButton value="false" className={classNames(classes.button, { error: switchValue === '' && childError })}>No</ToggleButton>,
                </ToggleButtonGroup>
    
        </FormControl>
    )
}

const PmsList: React.FC<IComponent> = props => {
    const classes = useStyles({ isMobile: props.isMobile });
    const [list, setList] = useState(null);
    const [ids, setIds] = useState([]);

    const localProps: ICustomProps = {} as ICustomProps;
    localProps.type = 'text';

    localProps.required = (props.hasOwnProperty('required') && props.required === true && !props.read_only);

    if (props.error) { localProps.error = true; }

    if (props.disabled) {
        localProps.disabled = true;
        localProps.buttonDisabled = 'disabled';
    }
        
    let baseClasses = 'item SortableItem rfb-item';
    if (props.editMode) { baseClasses += ' is-editing'; }
    if (!props.editMode) { baseClasses += ' not-is-editing'; }
    if (props.selected) { baseClasses += ' selected'; }

    const handleNoToAllClick = (event) => {
        ids.forEach((id) => {
            props.handleChange(event, id, 'false', props.id);
        }); 
    };

    const createRows = (fields) => {
        if (fields?.length === 0) return;

        const processedFields = fields.map(field => {
            const label = field.label ? field.label.split(':')[1]?.replace('-', '').replaceAll('"', '').replaceAll('*', '').trim() : '';
            const display = field.display ? field.display.split(':')[1]?.replace('-', '').replaceAll('"', '').replaceAll('*', '').trim() : '';
            const finalLabel = label || display || 'Unknown';
            const finalDisplay = display || 'Unknown';
            return {
                ...field,
                label: finalLabel,
                display: finalDisplay
            };
        });

        // Sort fields by their label, if label is missing sort by display
        processedFields.sort((a, b) => {
            const labelA = a.label || a.display;
            const labelB = b.label || b.display;
            return labelA.localeCompare(labelB);
        });

        setIds(processedFields.map(x => x.id));

        const rows = [];
        for (let i = 0; i < processedFields.length; i += 2) {
            const col1Label = processedFields[i].display && processedFields[i].display !== 'Unknown' ? processedFields[i].display : processedFields[i].label;
            let col2Label = '';
            if (i + 1 < processedFields.length) {
                col2Label = processedFields[i + 1].display && processedFields[i + 1].display !== 'Unknown' ? processedFields[i + 1].display : processedFields[i + 1].label;
            }
            rows.push(
                <div className={classes.row} key={`row-${i}`}>
                    <div className={classes.column1}>
                        <PmsListItem key={processedFields[i].id} id={processedFields[i].id} childComponents={props.childComponents} {...localProps} index={i} label={col1Label} value={processedFields[i].value} parentId={props.id} handleChange={props.handleChange} isMobile={props.isMobile} />
                    </div>
                    {processedFields[i + 1] && (
                        <div className={classes.column2}>
                            <PmsListItem key={processedFields[i + 1].id} id={processedFields[i + 1].id} childComponents={props.childComponents} {...localProps} index={i + 1} label={col2Label} value={processedFields[i + 1].value} parentId={props.id} handleChange={props.handleChange} isMobile={props.isMobile} />
                        </div>
                    )}
                </div>
            );
        }

        setList(rows);
    }

    useEffect(() => {

        if (list?.length || !props.pmsSystemVm)
            return;

        const fields = props.type === ALLERGIESPMS ? props.pmsSystemVm.allergyFormFields : props.pmsSystemVm.diseaseFormFields;

        if(fields !== undefined)
            createRows(fields.filter(x => x.htmlType === SWITCHINPUT));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.pmsSystemVm]);

    useEffect(() => {

        if (!props.childComponents)
            return;

        createRows(Object.values(props.childComponents));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    if (!list)
        return <></>;

    return (
        <div className={baseClasses}>
            <div className={classes.pmsListContainer}>
                <div className={classes.subHeader}>
                    <Typography variant={'h6'}>
                        {props.label}
                    </Typography>
                    <Button onClick={handleNoToAllClick} className={classes.noToAllButton} variant="outlined" disabled={localProps.disabled}>No to All</Button>
                </div>
                <div className={classes.pmsListItem}>
                    {list}
                </div>
            </div>
        </div>
    );
}

export default PmsList;



